import { useCallback, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { TEAM_NAME, PACKAGE_NAME, PROJECT_CREATE_APP_NAME } from '../../constants.tsx';
import type { CreateProjectResponse, CreateProjectPayload, CreateProjectBody } from './types.tsx';

export const constructEndpoint = () => '/rest/simplified/latest/project';

export const post = (payload: CreateProjectPayload): Promise<CreateProjectResponse> => {
	const url = constructEndpoint();
	const options = {
		body: JSON.stringify(payload),
	};
	return performPostRequest(url, options);
};

export type UseCreateProjectHook = [
	createProject: (body: CreateProjectBody) => Promise<CreateProjectResponse | undefined>,
	isLoading: boolean,
	hasError: boolean,
];

export const useCreateProject = (): UseCreateProjectHook => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);

	const createProject = useCallback(async ({ payload }: CreateProjectBody) => {
		setIsLoading(true);
		try {
			const result = await post(payload);
			setIsLoading(false);
			return result;
		} catch (error) {
			setHasError(true);
			setIsLoading(false);
			if (error instanceof Error) {
				fireErrorAnalytics({
					meta: {
						id: `${PROJECT_CREATE_APP_NAME}.use-create-project.error`,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error,
				});
			}
			throw error;
		}
	}, []);
	return [createProject, isLoading, hasError];
};
